/**
 * Parse a fetch Response body as a server-sent-event stream of JSON payloads.
 *
 * The backend emits `data: {json}\n\n` frames; chunks may split mid-line, so a
 * partial-line buffer is kept across reads. Malformed lines are skipped.
 * Aborting the fetch makes `reader.read()` reject with AbortError — callers
 * catch it around their `for await` loop.
 */
export async function* parseSSEStream<T>(response: Response): AsyncGenerator<T> {
  if (!response.body) return
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ""
  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split("\n")
      buffer = lines.pop() ?? ""
      for (const line of lines) {
        const trimmed = line.replace(/\r$/, "")
        if (!trimmed.startsWith("data: ")) continue
        try {
          yield JSON.parse(trimmed.slice(6)) as T
        } catch {
          // skip malformed frame
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
}
